import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { tv, VariantProps } from 'tailwind-variants'

import Icon, { IconsNames } from '@/components/Icon'
import { PopUpoShowTQRCodeProps } from '@/components/QRCode'
import { useAmplitude } from '@/contexts/AmplitudeProvider'
import { getDeviceId } from '@/services/amplitude/getDeviceId'
import cn from '@/utils/cn'
import { getAllExperimentsAndVariants } from '@/utils/getAllExperimentsAndVariants'
import { getGaClientId } from '@/utils/getGaClientId'
import { getGASessionId } from '@/utils/getGASessionId'

import {
  CMSTrackOnClickProps,
  CMSTrackOnClickProvider
} from '../CMSTrackOnClickProvider'
import ButtonOpenModalAccount from './components/ButtonOpenModalAccount'
import Loading from './components/Loading'

export const button = tv({
  base: 'btn w-full md:w-auto font-semibold whitespace-nowrap',
  variants: {
    color: {
      primary: 'btn-primary',
      secondary: 'btn-secondary',
      'primary-reverse': 'btn-primary-reverse'
    },
    size: {
      small: 'btn-small',
      regular: 'btn-regular',
      large: 'btn-large'
    }
  },
  defaultVariants: {
    size: 'large',
    color: 'primary'
  }
})

export const link = tv({
  base: 'flex gap-4 md:p-0 px-16 pb-8 w-max max-w-[620px] md:max-w-none text-[16px] font-medium leading-[24px] paragraph-16',
  variants: {
    color: {
      primary: 'text-newTon-700',
      secondary: 'text-newTon-300',
      'primary-reverse': 'text-display-900'
    }
  },
  defaultVariants: {
    color: 'primary'
  }
})

export type ButtonV2Props = {
  /* CMS props */
  label?: string
  color?: string
  size?: string
  linkPathname?: string
  linkTarget?: string
  linkQuery?: string
  linkHash?: string
  linkHref?: string
  iconName?: IconsNames
  iconPosition?: 'left' | 'right'
  handleAppDownload?: PopUpoShowTQRCodeProps
  uiStyleVariant?: 'link' | 'button'
  dynamicLink?: string

  /* Internal props */
  type?: 'button' | 'submit' | 'reset'
  isLoading?: boolean
  preFetch?: boolean
  disabled?: boolean
  className?: string
  onClick?: () => void
  children?: React.ReactNode
  ctaReference?: string
  typePlan?: string
} & CMSTrackOnClickProps &
  VariantProps<typeof button>

export function ButtonContent({
  isLoading,
  iconName,
  iconPosition,
  label,
  children
}: ButtonV2Props) {
  return (
    <Loading isLoading={!!isLoading}>
      {iconName && iconPosition === 'left' && (
        <Icon name={iconName} className="w-24 min-w-[24px] h-24 fill-current" />
      )}
      {label}
      {children}
      {iconName && iconPosition === 'right' && (
        <Icon name={iconName} className="w-24 min-w-[24px] h-24 fill-current" />
      )}
    </Loading>
  )
}

export function Button(props: ButtonV2Props) {
  const {
    color,
    size,
    preFetch = false,
    className = '',
    linkPathname,
    linkQuery,
    linkHash,
    linkHref,
    linkTarget,
    handleAppDownload = 'default',
    uiStyleVariant = 'button',
    trackOnClick,
    experiment,
    ctaReference,
    typePlan
  } = props

  const router = useRouter()
  const [queryParamsFromCms, setQueryParamsFromCms] = useState<
    Record<string, string>
  >({})
  const { analytics } = useAmplitude()

  useEffect(() => {
    const params = new URLSearchParams(linkQuery)
    const paramsObject = {} as Record<string, string>
    for (const [key, value] of params.entries()) {
      if (key !== 'null') {
        paramsObject[key] = value
      }
    }

    setQueryParamsFromCms(paramsObject)
  }, [linkQuery])

  return handleAppDownload === 'none' ? (
    <CMSTrackOnClickProvider
      trackOnClick={trackOnClick}
      experiment={experiment}
    >
      <Link
        href={
          linkHref || {
            pathname: linkPathname,
            query: { ...router.query, ...queryParamsFromCms },
            hash: linkHash
          }
        }
        target={linkTarget}
        prefetch={preFetch}
        className={cn(
          {
            [button({ color, size })]: uiStyleVariant === 'button',
            [link({ color })]: uiStyleVariant === 'link'
          },
          className
        )}
        onClick={(e) => {
          const textSection = (e.nativeEvent as unknown as { section: string })
            .section

          analytics?.track({
            event_type: 'cta ton',
            event_properties: {
              name: 'cta ton',
              section_reference: textSection,
              cross_lead_id: getGASessionId(),
              client_id_ga: getGaClientId(),
              user_agent: window?.navigator?.userAgent,
              category: `${window.location.href}`,
              cta_reference: ctaReference ? ctaReference : props.label,
              type: typePlan,
              source: `${e.currentTarget.href.split('/').pop()}`,
              experiments: JSON.stringify(getAllExperimentsAndVariants())
            },
            device_id: getDeviceId(),
            user_agent: window?.navigator?.userAgent
          })
        }}
      >
        <ButtonContent {...props} />
      </Link>
    </CMSTrackOnClickProvider>
  ) : (
    <ButtonOpenModalAccount {...props} />
  )
}

export default Button
